import { Difference } from '../differences/types';
import { DifferenceViewOptions } from 'types';

// Narrowed filter types based on DifferenceViewOptions
export type DifferenceTypeFilters = DifferenceViewOptions['differenceTypeFilters'];
export type DifferenceStatusFilters = DifferenceViewOptions['differenceStatusFilters'];

export const isDifferenceFilteredOut = (
  diff: Difference,
  typeFilters: DifferenceTypeFilters,
  statusFilters: DifferenceStatusFilters,
): boolean => {
  if (!typeFilters || !statusFilters) return false;

  return (
    typeFilters[diff.type] || // filtered out by type
    statusFilters[diff.statusId as string] || // filtered out by status
    (statusFilters?.smartDiscard && diff.smartDiscard) || // filtered out by smart discard
    (statusFilters?.unassigned && !diff.statusId) || // filtered out by unassigned
    diff.excluded // filtered out if excluded (used for merged differences)
  );
};

// Helper to get location for sorting (same logic as sortDifferences)
const getLocationForSorting = (difference: Difference) => {
  const isSourceOrTarget = difference.source?.location?.rect ? 'source' : 'target';
  const location = difference[isSourceOrTarget]?.location;
  if (!location) return { pageNumber: Infinity, y: Infinity, x: Infinity };
  return {
    pageNumber: location.pageNumber ?? Infinity,
    y: location.rect?.[1] ?? Infinity,
    x: location.rect?.[0] ?? Infinity,
  };
};

// Helper to compare locations (same logic as sortDifferences)
const compareLocations = (a: ReturnType<typeof getLocationForSorting>, b: ReturnType<typeof getLocationForSorting>) => {
  if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
  if (a.y !== b.y) return a.y - b.y;
  return a.x - b.x;
};

// Group differences by word/groupId while preserving original order
// Always selects the difference with the earliest reading order position as the representative
// This ensures groups maintain their position even when the input array order changes
export const groupDifferencesByGroupOrWord = (differences: Difference[]): Difference[] => {
  const alreadyGrouped: Set<string> = new Set();
  const alreadyGroupedWords: Set<string> = new Set();

  // Map to track the best (earliest reading order) representative for each group/word
  const groupRepresentatives: Map<string, { diff: Difference; location: ReturnType<typeof getLocationForSorting> }> =
    new Map();
  const wordRepresentatives: Map<string, { diff: Difference; location: ReturnType<typeof getLocationForSorting> }> =
    new Map();

  // First pass: find the best representative for each group/word (earliest reading order)
  differences.forEach((difference) => {
    if (difference.word) {
      const location = getLocationForSorting(difference);
      const existing = wordRepresentatives.get(difference.word);
      if (!existing || compareLocations(location, existing.location) < 0) {
        wordRepresentatives.set(difference.word, { diff: difference, location });
      }
    } else if (difference.groupId) {
      const location = getLocationForSorting(difference);
      const existing = groupRepresentatives.get(difference.groupId);
      if (!existing || compareLocations(location, existing.location) < 0) {
        groupRepresentatives.set(difference.groupId, { diff: difference, location });
      }
    }
  });

  // Second pass: filter to keep only the best representative of each group/word
  // This ensures groups always use the same representative regardless of input array order
  return differences.filter((difference) => {
    if (difference.word) {
      const representative = wordRepresentatives.get(difference.word);
      if (representative && representative.diff.id === difference.id) {
        if (alreadyGroupedWords.has(difference.word)) {
          return false;
        }
        alreadyGroupedWords.add(difference.word);
        return true;
      }
      return false;
    } else if (difference.groupId) {
      const representative = groupRepresentatives.get(difference.groupId);
      if (representative && representative.diff.id === difference.id) {
        if (alreadyGrouped.has(difference.groupId)) {
          return false;
        }
        alreadyGrouped.add(difference.groupId);
        return true;
      }
      return false;
    }
    return true;
  });
};

// Compute grouped, unfiltered differences count from an arbitrary list using provided filters (no module filtering)
export const countGroupedUnfilteredDifferences = (
  differences: Difference[],
  typeFilters: DifferenceTypeFilters,
  statusFilters: DifferenceStatusFilters,
): number => {
  const unfiltered = differences.filter((diff) => !isDifferenceFilteredOut(diff, typeFilters, statusFilters));
  return groupDifferencesByGroupOrWord(unfiltered).length;
};
